/**
 * Context Actions
 * 
 * Orchestration layer for context operations following the single mutation funnel pattern.
 * Similar to entityActions.ts - handles business logic, validation, and coordinates with other stores.
 * 
 * Pattern:
 * - Actions handle orchestration and business logic
 * - Call _applyLocal* helpers for actual state mutations
 * - No direct state mutations via set()
 */

import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('ContextActions');

export type ContextActions = ReturnType<typeof createContextActions>;

export const createContextActions = (get: any, set: any) => ({
  
  /**
   * ✅ REFACTOR: Add item to context - MANUAL ONLY
   * This is ONLY called when user explicitly clicks "Add to context" or similar UI action
   * Automatic context updates are handled by _handleActiveWindowChange() in contextStore
   */
  async addItemToContext(itemId: string): Promise<{ success: boolean; isDuplicate?: boolean; error?: string }> {
    console.log('[addItemToContext] Manually adding item to context:', JSON.stringify({ itemId }, null, 2));
    
    try {
      // Import stores dynamically to avoid circular deps
      const { useSpaceStore } = await import('../../../../stores/spaceStore');
      const useEntityStore = (await import('../../../../stores/entityStore')).default;
      
      const spaceState = useSpaceStore.getState();
      const entityState = useEntityStore.getState();
      
      console.log('[addItemToContext] Entity store check:', JSON.stringify({
        totalEntities: entityState.entities.length,
        lookingFor: itemId
      }, null, 2));
      
      // Check if it's a window
      const window = spaceState.windows[itemId];
      if (window) {
        log.log('[addItemToContext] Item is a window:', itemId);
        const entity = spaceState.getWindowEntity(itemId);
        
        if (!entity?.llmId) {
          return { success: false, error: 'Window has no entity with llmId' };
        }
        
        // If already referenced, promote from currentDoc → contextDoc if needed
        const existingRef = get().documentReferences.find((ref: any) => ref.llmId === entity.llmId);
        if (existingRef) {
          if (existingRef.documentRole === 'currentDoc') {
            get()._promoteToContextDoc(entity.llmId);
            log.log('[addItemToContext] Promoted currentDoc to contextDoc:', entity.llmId);
            return { success: true };
          }
          log.log('[addItemToContext] Already a contextDoc:', entity.llmId);
          return { success: true, isDuplicate: true };
        }
        
        get()._applyAddDocumentReference(entity.llmId, {
          title: entity.name || window.title,
          appType: window.appType || 'docs',
          entityType: entity.entityType || 'Doc',
          windowId: itemId,
          documentRole: 'contextDoc',
          isActive: false
        });
        
        log.log('[addItemToContext] Window added to context as contextDoc:', entity.llmId);
        return { success: true };
      }
      
      // Check if it's an entity in entityStore
      const entity = entityState.entities.find((e: any) => 
        e.id === itemId || e._id === itemId || e.llmId === itemId
      );
      
      if (entity) {
        console.log('[addItemToContext] Found entity:', JSON.stringify({
          entityId: entity._id,
          entityType: entity.entityType,
          name: entity.name,
          llmId: entity.llmId,
          hasSkeleton: !!entity.skeleton,
          skeletonKeys: entity.skeleton ? Object.keys(entity.skeleton) : []
        }, null, 2));
        
        // For Doc entities, use document reference system
        if (entity.entityType === 'Doc' && entity.llmId) {
          const existingRef = get().documentReferences.find((ref: any) => ref.llmId === entity.llmId);
          if (existingRef) {
            if (existingRef.documentRole === 'currentDoc') {
              get()._promoteToContextDoc(entity.llmId);
              log.log('[addItemToContext] Promoted currentDoc to contextDoc:', entity.llmId);
              return { success: true };
            }
            log.log('[addItemToContext] Already a contextDoc:', entity.llmId);
            return { success: true, isDuplicate: true };
          }
          
          get()._applyAddDocumentReference(entity.llmId, {
            title: entity.name,
            appType: 'docs',
            entityType: entity.entityType,
            windowId: undefined,
            documentRole: 'contextDoc',
            isActive: false
          });
          
          log.log('[addItemToContext] Doc entity added to context as contextDoc:', entity.llmId);
          return { success: true };
        }
        
        // For other entities, use entity context system
        const existingEntity = get().entityContexts.find((ctx: any) => ctx.entityId === (entity._id || itemId));
        if (existingEntity) {
          if (existingEntity.metadata?.addedBy === 'automatic') {
            get()._promoteEntityContextToManual(entity._id || itemId);
            log.log('[addItemToContext] Promoted automatic entity context to manual:', entity._id);
            return { success: true };
          }
          log.log('[addItemToContext] Already a manual entity context:', entity._id);
          return { success: true, isDuplicate: true };
        }
        
        // Extract entity-specific content
        const { extractEntityContent } = await import('../../../../utils/entityContentExtractor');
        
        get()._applyAddEntityContext({
          entityId: entity._id || itemId,
          entityType: entity.entityType,
          name: entity.name,
          llmId: entity.llmId,
          content: extractEntityContent(entity),
          metadata: {
            addedBy: 'manual', // ✅ REFACTOR: Always manual
            contentType: entity.entityType.toLowerCase()
          }
        });
        
        log.log('[addItemToContext] ✅ Entity added to context:', entity._id);
        return { success: true };
      }
      
      // Check if it's an entity in entityStore (by llmId)
      const spaceEntity = entityState.entities.find((e: any) => e.llmId === itemId);
      if (spaceEntity) {
        log.log('[addItemToContext] Item is an entity:', itemId);
        
        const existingRef = get().documentReferences.find((ref: any) => ref.llmId === itemId);
        if (existingRef) {
          if (existingRef.documentRole === 'currentDoc') {
            get()._promoteToContextDoc(itemId);
            log.log('[addItemToContext] Promoted currentDoc to contextDoc:', itemId);
            return { success: true };
          }
          log.log('[addItemToContext] Already a contextDoc:', itemId);
          return { success: true, isDuplicate: true };
        }
        
        get()._applyAddDocumentReference(itemId, {
          title: spaceEntity.name,
          appType: 'docs',
          entityType: spaceEntity.entityType,
          windowId: undefined,
          documentRole: 'contextDoc',
          isActive: false
        });
        
        log.log('[addItemToContext] Space entity added to context as contextDoc:', itemId);
        return { success: true };
      }
      
      // If we can't identify it, return an error
      log.error('[addItemToContext] Could not identify item type for ID:', itemId);
      return { success: false, error: `Could not identify item type for ID: ${itemId}` };
      
    } catch (error) {
      log.error('[addItemToContext] Error adding item to context:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : String(error) 
      };
    }
  },
  
  /**
   * Remove item from context
   */
  removeFromContext(llmId: string, reason?: string): void {
    log.log('[removeFromContext] Removing item:', { llmId, reason });
    
    // Use internal helper for mutation
    get()._applyRemoveDocumentReference(llmId, {
      reason: reason || 'manual_removal'
    });
    
    log.log('[removeFromContext] ✅ Item removed from context:', llmId);
  },
  
  /**
   * Add explicit content (text selection, pasted content)
   */
  addExplicitContent(content: {
    text: string;
    sourceType: 'selection' | 'paste';
    sourceDocument?: { llmId: string; title: string };
    clearAfterSubmit?: boolean;
    metadata?: any;
  }): string {
    log.log('[addExplicitContent] Adding explicit content:', {
      textLength: content.text.length,
      sourceType: content.sourceType,
      hasSourceDocument: !!content.sourceDocument
    });
    
    // Use internal helper
    const contentId = get()._applyAddExplicitContent(content);
    
    log.log('[addExplicitContent] ✅ Explicit content added:', contentId);
    return contentId;
  },
  
  /**
   * Remove explicit content by ID
   */
  removeExplicitContent(id: string): void {
    log.log('[removeExplicitContent] Removing explicit content:', id);
    get()._applyRemoveExplicitContent(id);
    log.log('[removeExplicitContent] ✅ Explicit content removed:', id);
  },
  
  /**
   * Add entity context (dragged entities, pills)
   */
  async addEntityContext(entityContext: {
    entityId: string;
    entityType: string;
    name: string;
    llmId?: string;
    content: string;
    metadata?: any;
  }): Promise<void> {
    console.log('[addEntityContext] Adding entity context:', JSON.stringify({
      entityId: entityContext.entityId,
      entityType: entityContext.entityType,
      name: entityContext.name,
      contentLength: entityContext.content?.length,
      contentPreview: entityContext.content?.substring(0, 100)
    }, null, 2));
    
    // Check for duplicates
    if (get().hasEntityContext(entityContext.entityId)) {
      log.log('[addEntityContext] Duplicate detected:', entityContext.entityId);
      return;
    }
    
    // ✅ IMPORTANT: If content looks like the old default format, try to re-extract it
    const defaultFormatRegex = /^[\w.]+:\s*.+$/; // Matches "EntityType: Name" or "Entity: Type (Name)"
    if (defaultFormatRegex.test(entityContext.content.trim())) {
      console.log('[addEntityContext] ⚠️ Content looks like old default format, attempting to re-extract');
      
      try {
        // Try to get the full entity from entityStore
        const useEntityStore = (await import('../../../../stores/entityStore')).default;
        const entityState = useEntityStore.getState();
        const fullEntity = entityState.entities.find((e: any) => 
          e._id === entityContext.entityId || 
          e.llmId === entityContext.llmId
        );
        
        if (fullEntity) {
          const { extractEntityContent } = await import('../../../../utils/entityContentExtractor');
          entityContext.content = extractEntityContent(fullEntity);
          
          console.log('[addEntityContext] Re-extracted content:', JSON.stringify({
            newContentLength: entityContext.content.length,
            newContentPreview: entityContext.content.substring(0, 200)
          }, null, 2));
        } else {
          console.warn('[addEntityContext] Could not find full entity to re-extract:', entityContext.entityId);
        }
      } catch (error) {
        console.warn('[addEntityContext] Failed to re-extract content:', error);
      }
    }
    
    get()._applyAddEntityContext(entityContext);
    log.log('[addEntityContext] ✅ Entity context added:', entityContext.entityId);
  },
  
  /**
   * Remove entity context
   */
  removeEntityContext(entityId: string): void {
    log.log('[removeEntityContext] Removing entity context:', entityId);
    get()._applyRemoveEntityContext(entityId);
    log.log('[removeEntityContext] ✅ Entity context removed:', entityId);
  },
  
  /**
   * Add file context (uploaded files)
   */
  addFileContext(fileContext: {
    fileName: string;
    fileSize: number;
    fileType: string;
    mimeType: string;
    content: string;
    file?: File;
    metadata?: any;
  }): void {
    log.log('[addFileContext] Adding file context:', fileContext.fileName);
    get()._applyAddFileContext(fileContext);
    log.log('[addFileContext] ✅ File context added:', fileContext.fileName);
  },
  
  /**
   * Remove file context
   */
  removeFileContext(fileName: string): void {
    log.log('[removeFileContext] Removing file context:', fileName);
    get()._applyRemoveFileContext(fileName);
    log.log('[removeFileContext] ✅ File context removed:', fileName);
  },
  
  /**
   * Clear all context
   */
  clearAllContext(): void {
    log.log('[clearAllContext] Clearing all context');
    
    set({
      documentReferences: [],
      explicitContent: [],
      entityContexts: [],
      fileContexts: []
    });
    
    log.log('[clearAllContext] ✅ All context cleared');
  },
  
  /**
   * Add current document to context (active window)
   */
  addCurrentDocumentToContext(): void {
    log.log('[addCurrentDocumentToContext] Adding current document');
    
    // Delegate to existing method
    get().addCurrentDocumentToContext();
    
    log.log('[addCurrentDocumentToContext] ✅ Current document added');
  }
});

